import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  alpha,
  Avatar,
  ListItemText,
  ListItemIcon,
  IconButton,
  useTheme,
  Stack, // Added Stack for button layout
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Send, CheckCircle, Group, Cancel } from "@mui/icons-material"; // Added Cancel icon
import { url } from "../constants/url";
import { useThemeToggle } from "../context/ThemeContext";
import CustomAlert from "../components/customAlert";

const AssignTeamMemberLead = ({ userData, leadId, onClose, onSuccess }) => {
  const theme = useTheme();
  const { mode } = useThemeToggle();
  const companyId = userData?.companyAdded?.[0]?.companyId;

  const [companyMembersData, setCompanyMembersData] = useState([]);
  const [teamMemberId, setTeamMemberId] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingMembers, setFetchingMembers] = useState(true);

  // Alert state
  const [alert, setAlert] = useState({
    open: false,
    severity: "success",
    message: ""
  });

  const primaryColor = theme.palette.primary.main;
  const successColor = theme.palette.success.main;
  const errorColor = theme.palette.error.main;

  const fetchTeamMembers = async () => {
    setFetchingMembers(true);
    try {
      const response = await fetch(
        `${url}/api/company/fetch-company-members?companyId=${companyId}`
      );
      const data = await response.json();

      if (data.data?.teamMembers) {
        setCompanyMembersData(data.data.teamMembers);
      } else {
        setCompanyMembersData([]);
      }
    } catch (error) {
      console.error("❌ Error:", error);
      showAlert("error", "Failed to load team members");
    }
    setFetchingMembers(false);
  };

  // Show alert function
  const showAlert = (severity, message) => {
    setAlert({
      open: true,
      severity,
      message
    });
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, severity: "success", message: "" });
  };

  const handleAssignTeamMemberLead = async () => {
    if (!teamMemberId) {
      showAlert("error", "Please select a team member");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${url}/api/admin/assign-client-to-team-member`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: leadId,
          teamMemberId,
          adminId: userData._id,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showAlert("success", "Lead assigned successfully");
        
        // Reset form
        setTeamMemberId("");
        
        // Close modal after delay
        setTimeout(() => {
          onClose();
          // Call onSuccess callback if provided to refresh leads data
          if (onSuccess) {
            onSuccess();
          }
        }, 1500);
      } else {
        showAlert("error", data.message || "Error assigning lead");
      }
    } catch (error) {
      showAlert("error", "Something went wrong");
    }

    setLoading(false);
  };

  const handleCancel = () => {
    onClose();
  };

  useEffect(() => {
    if (companyId) fetchTeamMembers();
  }, [companyId]);

  return (
    <>
      <Box sx={{ 
        p: 0, 
        m: 0, 
        bgcolor: mode === 'dark' ? theme.palette.background.paper : 'white' 
      }}>
        <Card
          sx={{
            borderRadius: 0,
            bgcolor: mode === 'dark' ? theme.palette.background.paper : 'white',
            boxShadow: 'none',
            border: 'none',
            position: "relative",
          }}
        >
          {/* Top-right close button */}
          <IconButton
            onClick={onClose}
            sx={{ 
              position: "absolute", 
              top: 8, 
              right: 8,
              zIndex: 1,
              color: mode === 'dark' ? theme.palette.text.primary : 'inherit'
            }}
          >
            <CloseIcon />
          </IconButton>

          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ 
                mb: 3, 
                textAlign: "center",
                color: mode === 'dark' ? theme.palette.text.primary : 'text.primary'
              }}
            >
              Assign Lead
            </Typography>

            {/* Select Team Member */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel
                sx={{
                  color: mode === 'dark' ? theme.palette.text.secondary : 'inherit'
                }}
              >
                Select Team Member
              </InputLabel>
              <Select
                value={teamMemberId}
                label="Select Team Member"
                onChange={(e) => setTeamMemberId(e.target.value)}
                sx={{
                  backgroundColor: mode === 'dark' ? theme.palette.background.default : '#f5f5f5',
                  color: mode === 'dark' ? theme.palette.text.primary : 'inherit',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: mode === 'dark' ? `1px solid ${theme.palette.grey[700]}` : '1px solid #e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: mode === 'dark' ? `1px solid ${theme.palette.grey[500]}` : '1px solid #bdbdbd',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${primaryColor}`,
                  },
                  '& .MuiSelect-icon': {
                    color: mode === 'dark' ? theme.palette.text.secondary : 'inherit',
                  }
                }}
                renderValue={(selected) => {
                  const member = companyMembersData.find(
                    (m) => m.userId?._id === selected
                  );
                  return member
                    ? `${member.userId?.name || member.userId?.email}`
                    : "Select team member";
                }}
              >
                {fetchingMembers ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} />
                    &nbsp;Loading…
                  </MenuItem>
                ) : companyMembersData.length ? (
                  companyMembersData.map((member) => (
                    <MenuItem 
                      value={member.userId?._id} 
                      key={member._id}
                      sx={{
                        backgroundColor: mode === 'dark' ? theme.palette.background.paper : 'white',
                        '&:hover': {
                          backgroundColor: mode === 'dark' ? theme.palette.action.hover : theme.palette.action.hover,
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ 
                          bgcolor: primaryColor,
                          width: 32,
                          height: 32,
                          fontSize: '0.875rem'
                        }}>
                          {(member.userId?.name || "U")[0].toUpperCase()}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={member.userId?.name}
                        secondary={member.userId?.email}
                        primaryTypographyProps={{
                          sx: { color: mode === 'dark' ? theme.palette.text.primary : 'inherit' }
                        }}
                        secondaryTypographyProps={{
                          sx: { color: mode === 'dark' ? theme.palette.text.secondary : 'inherit' }
                        }}
                      />
                      {teamMemberId === member.userId?._id && (
                        <CheckCircle sx={{ color: successColor }} />
                      )}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Group sx={{ mr: 1 }} />
                    No team members found
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Buttons - Side by Side */}
            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              {/* Cancel Button */}
              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={handleCancel}
                disabled={loading}
                startIcon={<Cancel />}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: 1,
                  textTransform: "none",
                  borderColor: errorColor,
                  color: errorColor,
                  '&:hover': {
                    backgroundColor: alpha(errorColor, 0.04),
                    borderColor: errorColor,
                    boxShadow: `0 2px 8px ${alpha(errorColor, 0.2)}`,
                  },
                  '&:disabled': {
                    borderColor: alpha(errorColor, 0.3),
                    color: alpha(errorColor, 0.3),
                  }
                }}
              >
                Cancel
              </Button>

              {/* Assign Button */}
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleAssignTeamMemberLead}
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <Send />
                }
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: 1,
                  textTransform: "none",
                  backgroundColor: successColor,
                  '&:hover': {
                    backgroundColor: alpha(successColor, 0.9),
                    boxShadow: `0 2px 8px ${alpha(successColor, 0.3)}`,
                  },
                  '&:disabled': {
                    backgroundColor: alpha(successColor, 0.6),
                  }
                }}
              >
                {loading ? "Assigning..." : "Assign Lead"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Custom Alert */}
      <CustomAlert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        onClose={handleCloseAlert}
      />
    </>
  );
};

export default AssignTeamMemberLead;